import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, BrowserRouter as Router, useNavigate, useLocation } from 'react-router-dom';
import { DistanceUnitProvider } from './contexts/DistanceUnitContext';
import { SyncStatusProvider } from './contexts/SyncStatusContext';
import LandingPage from './components/LandingPage';
import Register from './components/Register';
import ProfileNew from './components/ProfileNew';
import StravaCallback from './components/StravaCallback';
import Dashboard from './components/Dashboard';
import Activities from './components/Activities';
import CookieConsent from './components/CookieConsent';
import SyncStatusBar from './components/SyncStatusBar';
import CurrentStreak from './components/CurrentStreak';
import LongestStreak from './components/LongestStreak';
import TotalRunDays from './components/TotalRunDays';
import Friends from './components/Friends';
import Leaderboard from './components/Leaderboard';
import FriendProfile from './components/FriendProfile';
import Analytics from './components/Analytics';
import Help from './components/Help';
import Pricing from './components/Pricing';
import About from './components/About';
import Privacy from './components/Privacy';
import Terms from './components/Terms';
import VerifyEmail from './components/VerifyEmail';
import Sidebar from './components/Sidebar';
import ProtectedRoute from './components/ProtectedRoute';
import { StravaModalProvider } from './contexts/StravaModalContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { initGA, trackPageView } from './utils/analytics';
import AdminDashboard from './components/AdminDashboard';

function Navigation() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkLoginStatus = () => {
      const user = localStorage.getItem('user');
      setIsLoggedIn(!!user);
    };

    // Check on mount
    checkLoginStatus();

    // Listen for storage changes (including from other tabs/windows)
    window.addEventListener('storage', checkLoginStatus);

    // Listen for custom login event
    window.addEventListener('login', checkLoginStatus);

    return () => {
      window.removeEventListener('storage', checkLoginStatus);
      window.removeEventListener('login', checkLoginStatus);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setIsLoggedIn(false);
    navigate('/');
  };

  // Don't show navigation on landing page when logged out, new dashboard, activities, analytics, leaderboard, friends, profile, help, verify-email, or strava-callback page
  if (
    (location.pathname === '/' && !isLoggedIn) ||
    location.pathname === '/dashboard' ||
    location.pathname === '/activities' ||
    location.pathname === '/analytics' ||
    location.pathname === '/leaderboard' ||
    location.pathname === '/friends' ||
    location.pathname === '/profile' ||
    location.pathname === '/help' ||
    location.pathname === '/verify-email' ||
    location.pathname === '/strava-callback' ||
    location.pathname.startsWith('/admin')
  ) {
    return null;
  }

  return (
    <header style={{ backgroundColor: '#14b8a6', padding: '10px 0' }}>
      <nav>
        <ul style={{ listStyle: 'none', display: 'flex', gap: '20px', padding: '0 20px', margin: 0, alignItems: 'center' }}>
          {isLoggedIn && (
            <>
              <li>
                <Link to="/dashboard" style={{ color: 'white', textDecoration: 'none', fontSize: '16px' }}>Dashboard</Link>
              </li>
              <li>
                <Link to="/profile" style={{ color: 'white', textDecoration: 'none', fontSize: '16px' }}>Profile</Link>
              </li>
              <li>
                <Link to="/activities" style={{ color: 'white', textDecoration: 'none', fontSize: '16px' }}>Activities</Link>
              </li>
              <li>
                <Link to="/friends" style={{ color: 'white', textDecoration: 'none', fontSize: '16px' }}>Friends</Link>
              </li>
              <li>
                <Link to="/leaderboard" style={{ color: 'white', textDecoration: 'none', fontSize: '16px' }}>Leaderboard</Link>
              </li>
              <li>
                <Link to="/analytics" style={{ color: 'white', textDecoration: 'none', fontSize: '16px' }}>Analytics</Link>
              </li>
              <li>
                <button
                  onClick={handleLogout}
                  style={{
                    color: 'white',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '400',
                    padding: 0
                  }}>
                  Logout
                </button>
              </li>
            </>
          )}
        </ul>
      </nav>
    </header>
  );
}

function AppContent() {
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Initialize Google Analytics on mount
  useEffect(() => {
    initGA();
  }, []);

  // Track page views on route change
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  useEffect(() => {
    const checkLoginStatus = () => {
      const user = localStorage.getItem('user');
      setIsLoggedIn(!!user);
    };

    checkLoginStatus();
    window.addEventListener('storage', checkLoginStatus);
    window.addEventListener('login', checkLoginStatus);

    return () => {
      window.removeEventListener('storage', checkLoginStatus);
      window.removeEventListener('login', checkLoginStatus);
    };
  }, []);

  // Define routes where old sidebar should be shown (exclude new dashboard, new activities, new analytics, new leaderboard, and new friends)
  const sidebarRoutes = ['/streak'];
  const showSidebar = isLoggedIn && sidebarRoutes.some(route => location.pathname.startsWith(route));
  const showSyncStatusBar = location.pathname !== '/dashboard' && location.pathname !== '/activities' && location.pathname !== '/analytics' && location.pathname !== '/leaderboard' && location.pathname !== '/friends' && location.pathname !== '/profile' && location.pathname !== '/help' && location.pathname !== '/strava-callback' && location.pathname !== '/verify-email';

  return (
    <div className="App">
      <Navigation />
      {showSyncStatusBar && <SyncStatusBar />}
      {showSidebar && <Sidebar />}
      <main className={showSidebar ? 'app-with-sidebar' : ''}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage/>} />
          <Route path="/register" element={<Register/>} />
          <Route path="/verify-email" element={<VerifyEmail/>} />
          <Route path="/strava-callback" element={<StravaCallback/>} />
          <Route path="/pricing" element={<Pricing/>} />
          <Route path="/about" element={<About/>} />
          <Route path="/privacy" element={<Privacy/>} />
          <Route path="/terms" element={<Terms/>} />
          <Route path="/admin" element={<AdminDashboard />} />

          {/* Protected routes - require authentication */}
          <Route path="/profile" element={<ProtectedRoute><ProfileNew user={JSON.parse(localStorage.getItem('user') || '{}')} onLogout={() => { localStorage.removeItem('user'); localStorage.removeItem('token'); window.location.href = '/'; }}/></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard onLogout={() => { localStorage.removeItem('user'); localStorage.removeItem('token'); window.location.href = '/'; }}/></ProtectedRoute>} />
          <Route path="/activities" element={<ProtectedRoute><Activities/></ProtectedRoute>} />
          <Route path="/streak/current" element={<ProtectedRoute><CurrentStreak/></ProtectedRoute>} />
          <Route path="/streak/longest" element={<ProtectedRoute><LongestStreak/></ProtectedRoute>} />
          <Route path="/streak/total" element={<ProtectedRoute><TotalRunDays/></ProtectedRoute>} />
          <Route path="/friends" element={<ProtectedRoute><Friends user={JSON.parse(localStorage.getItem('user') || '{}')} onLogout={() => { localStorage.removeItem('user'); localStorage.removeItem('token'); window.location.href = '/'; }}/></ProtectedRoute>} />
          <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard user={JSON.parse(localStorage.getItem('user') || '{}')} onLogout={() => { localStorage.removeItem('user'); localStorage.removeItem('token'); window.location.href = '/'; }}/></ProtectedRoute>} />
          <Route path="/friend/:userId" element={<ProtectedRoute><FriendProfile/></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute><Analytics user={JSON.parse(localStorage.getItem('user') || '{}')} onLogout={() => { localStorage.removeItem('user'); localStorage.removeItem('token'); window.location.href = '/'; }}/></ProtectedRoute>} />
          <Route path="/help" element={<ProtectedRoute><Help user={JSON.parse(localStorage.getItem('user') || '{}')} onLogout={() => { localStorage.removeItem('user'); localStorage.removeItem('token'); window.location.href = '/'; }}/></ProtectedRoute>} />
        </Routes>
      </main>
      <CookieConsent />
    </div>
  );
}

function App() {
  return (
    <Router>
      <SyncStatusProvider>
        <DistanceUnitProvider>
          <NotificationProvider>
            <StravaModalProvider>
              <AppContent />
            </StravaModalProvider>
          </NotificationProvider>
        </DistanceUnitProvider>
      </SyncStatusProvider>
    </Router>
  );
}

export default App;
